import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  PointElement,
  LineElement,
  Filler,
  RadialLinearScale
} from 'chart.js';
import { Pie, Bar, Line, Radar } from 'react-chartjs-2';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import './TestResult.css';
import './iQuantaStyle.css';

import {
  Container,
  Typography,
  Grid,
  Paper,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  CircularProgress,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  AlertTitle,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Rating,
  Stack,
  IconButton,
  Tooltip as MuiTooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Fade,
  Zoom,
  Grow,
  Slide,
  Fab,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
  Drawer,
  ListItemButton,
  Collapse,
  Skeleton,
  Backdrop,
  CircularProgress as MuiCircularProgress,
  Popper,
  ClickAwayListener,
  MenuList,
  MenuItem,
  Badge,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Timer,
  TrendingUp,
  TrendingDown,
  EmojiEvents,
  School,
  Speed,
  Psychology,
  Analytics,
  Timeline,
  Insights,
  Lightbulb,
  Star,
  Warning,
  Info,
  ExpandMore,
  PlayArrow,
  Assignment,
  Grade,
  Leaderboard,
  Schedule,
  Target,
  Assessment,
  Book,
  TrendingUpOutlined,
  SpeedOutlined,
  TimerOutlined,
  PsychologyOutlined,
  AutoGraph,
  WorkspacePremium,
  RocketLaunch,
  TrendingFlat,
  ZoomIn,
  ZoomOut,
  Fullscreen,
  FullscreenExit,
  MoreVert,
  FilterList,
  Sort,
  Download,
  Share,
  Print,
  Visibility,
  VisibilityOff,
  Compare,
  AccessTime,
  Help,
  Settings,
  Refresh,
  GetApp
} from '@mui/icons-material';
import { useTheme } from '../ThemeContext';

// Register ChartJS components
ChartJS.register(
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale, 
  LinearScale, 
  BarElement, 
  Title,
  PointElement,
  LineElement,
  Filler,
  RadialLinearScale
);

const TestResult = () => {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [questionDetailOpen, setQuestionDetailOpen] = useState(false);
  const [subjectDetailOpen, setSubjectDetailOpen] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [topicDetailOpen, setTopicDetailOpen] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [timeAnalysisOpen, setTimeAnalysisOpen] = useState(false);
  const [fullscreenChart, setFullscreenChart] = useState(false);
  const [chartType, setChartType] = useState('pie');
  const [showAdvancedMetrics, setShowAdvancedMetrics] = useState(false);
  const [comparisonMode, setComparisonMode] = useState(false);
  const [speedDialOpen, setSpeedDialOpen] = useState(false);
  const [hoveredElement, setHoveredElement] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [filteredQuestions, setFilteredQuestions] = useState([]);
  const [sortBy, setSortBy] = useState('questionNumber');
  const [sortOrder, setSortOrder] = useState('asc');
  const [aiInsights, setAiInsights] = useState(null);
  const [aiInsightsLoading, setAiInsightsLoading] = useState(false);
  const [aiInsightsError, setAiInsightsError] = useState(null);
  const [insightsVisible, setInsightsVisible] = useState(false);
  const { id } = useParams();
  const { mode } = useTheme();
  const insightsRef = useRef(null);

  useEffect(() => {
    const fetchResult = async () => {
      try {
        const token = localStorage.getItem('token');
        const config = {
          headers: { Authorization: `Bearer ${token}` },
        };
        
        const res = await axios.get(`/api/results/${id}`, config);
        setResult(res.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching result:', error);
        setError('An error occurred while fetching the test result');
        setLoading(false);
      }
    };

    fetchResult();
  }, [id]);

  // Intersection Observer for lazy loading AI insights
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && insightsVisible && result && !aiInsights && !aiInsightsLoading) {
            generateAIInsights();
          }
        });
      },
      { threshold: 0.1 }
    );

    if (insightsRef.current) {
      observer.observe(insightsRef.current);
    }

    return () => {
      if (insightsRef.current) {
        observer.unobserve(insightsRef.current);
      }
    };
  }, [insightsVisible, result, aiInsights, aiInsightsLoading]);

  // Load AI insights when insights become visible
  useEffect(() => {
    if (insightsVisible && result && !aiInsights && !aiInsightsLoading) {
      generateAIInsights();
    }
  }, [insightsVisible, result, aiInsights, aiInsightsLoading]);
  
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mt: 2 }}>
          <AlertTitle>Error</AlertTitle>
          {error}
        </Alert>
      </Container>
    );
  }

  if (!result) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="info" sx={{ mt: 2 }}>
          <AlertTitle>No Result Found</AlertTitle>
          The requested test result could not be found.
        </Alert>
      </Container>
    );
  }

  // Format time from HH:MM:SS to MM:SS
  const formatTimeFromString = (timeString) => {
    if (!timeString) return 'N/A';
    const parts = timeString.split(':');
    if (parts.length === 3) {
      const hours = parseInt(parts[0]);
      const minutes = parseInt(parts[1]);
      const seconds = parseInt(parts[2]);
      const totalMinutes = hours * 60 + minutes;
      return `${totalMinutes}m ${seconds}s`;
    }
    return timeString;
  };

  // Calculate accuracy percentage
  const calculateAccuracy = () => {
    if (!result.attempted || result.attempted === 0) return 0;
    return (result.correct / result.attempted) * 100;
  };

  // Calculate sum of individual question timings
  const calculateTotalQuestionTime = () => {
    if (!result.per_question_time || result.per_question_time.length === 0) {
      return 0;
    }
    return result.per_question_time.reduce((total, timing) => total + (timing.timeSpent || 0), 0);
  };

  const totalQuestionTime = calculateTotalQuestionTime();
  const accuracy = calculateAccuracy();

  // Get performance level
  const getPerformanceLevel = (score) => {
    if (score >= 90) return 'Expert';
    if (score >= 80) return 'Advanced';
    if (score >= 70) return 'Intermediate';
    if (score >= 60) return 'Beginner';
    return 'Novice';
  };

  const getPerformanceColor = (score) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'error';
  };

  const getTrendIcon = (current, previous) => {
    if (!previous) return <TrendingFlat />;
    if (current > previous) return <TrendingUp color="success" />;
    if (current < previous) return <TrendingDown color="error" />;
    return <TrendingFlat />;
  };

  // Enhanced helper functions
  const handleQuestionClick = (question) => {
    setSelectedQuestion(question);
    setQuestionDetailOpen(true);
  };

  const handleSubjectClick = (subjectName, subjectData) => {
    setSelectedSubject({ name: subjectName, data: subjectData });
    setSubjectDetailOpen(true);
  };

  const handleTopicClick = (topicName, topicData) => {
    setSelectedTopic({ name: topicName, data: topicData });
    setTopicDetailOpen(true);
  };

  const handleTimeAnalysisClick = () => {
    setTimeAnalysisOpen(true);
  };

  const getPerformanceInsight = (score) => {
    if (score >= 90) return { level: 'Excellent', color: 'success', icon: <Star /> };
    if (score >= 80) return { level: 'Very Good', color: 'success', icon: <CheckCircle /> };
    if (score >= 70) return { level: 'Good', color: 'warning', icon: <TrendingUp /> };
    if (score >= 60) return { level: 'Average', color: 'warning', icon: <TrendingFlat /> };
    return { level: 'Needs Improvement', color: 'error', icon: <TrendingDown /> };
  };

  const getTimeInsight = (timeSpent, difficulty) => {
    const avgTime = difficulty === 'easy' ? 30 : difficulty === 'medium' ? 60 : 90;
    if (timeSpent < avgTime * 0.7) return { insight: 'Very Fast', color: 'success' };
    if (timeSpent < avgTime) return { insight: 'Good Pace', color: 'info' };
    if (timeSpent < avgTime * 1.3) return { insight: 'Slightly Slow', color: 'warning' };
    return { insight: 'Too Slow', color: 'error' };
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'easy': return 'success';
      case 'medium': return 'warning';
      case 'hard': return 'error';
      default: return 'default';
    }
  };

  const formatTime = (seconds) => {
    if (seconds < 60) return `${Math.round(seconds)}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.round(seconds % 60);
    return `${minutes}m ${remainingSeconds}s`;
  };

  const speedDialActions = [
    { icon: <GetApp />, name: 'Download Report', action: () => console.log('Download') },
    { icon: <Share />, name: 'Share Results', action: () => console.log('Share') },
    { icon: <Print />, name: 'Print Report', action: () => console.log('Print') },
    { icon: <Compare />, name: 'Compare Tests', action: () => setComparisonMode(!comparisonMode) },
    { icon: <Settings />, name: 'Settings', action: () => setDrawerOpen(true) }
  ];

  // AI Insights generation
  const generateAIInsights = async () => {
    if (aiInsights || aiInsightsLoading) return;
    
    setAiInsightsLoading(true);
    setAiInsightsError(null);
    
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: { Authorization: `Bearer ${token}` },
      };

      // Prepare test data for AI analysis
      const testData = {
        score: result.score || 0,
        totalScore: result.total_score || 0,
        percentage: result.perc_score || 0,
        accuracy: accuracy,
        correct: result.correct || 0,
        wrong: result.wrong || 0,
        unattempted: result.unattempted || 0,
        totalQuestions: result.totalQuestions || 0,
        timeSpent: result.time_spent,
        subjects: result.subjectWisePerformance || {},
        topics: result.topicWisePerformance || {},
        testName: result.TestMaster?.name || 'Test',
        difficulty: result.test_difficulty_level || 'medium'
      };

      const prompt = `Analyze this test result and provide personalized insights:

Test: ${testData.testName}
Score: ${testData.score}/${testData.totalScore} (${testData.percentage}%)
Accuracy: ${testData.accuracy.toFixed(1)}%
Correct: ${testData.correct}, Wrong: ${testData.wrong}, Unattempted: ${testData.unattempted}
Time Spent: ${testData.timeSpent}
Difficulty Level: ${testData.difficulty}

Subject Performance:
${Object.entries(testData.subjects).map(([subject, score]) => `- ${subject}: ${score}%`).join('\n')}

Topic Performance:
${Object.entries(testData.topics).slice(0, 5).map(([topic, score]) => `- ${topic}: ${score}%`).join('\n')}

Please provide:
1. Performance Analysis (2-3 sentences)
2. Subject Focus Recommendations (identify weakest areas)
3. Time Management Assessment
4. Specific Next Steps (3-4 actionable items)
5. Study Strategy Suggestions

Format as JSON with keys: performanceAnalysis, subjectFocus, timeManagement, nextSteps, studyStrategy`;

      const response = await axios.post('/api/ai/chat', {
        messages: [
          {
            role: 'user',
            content: prompt
          }
        ]
      }, config);

      // Parse the AI response
      let parsedInsights;
      try {
        // Try to extract JSON from the response
        const content = response.data.content;
        const jsonMatch = content.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          const rawParsed = JSON.parse(jsonMatch[0]);
          
          // Ensure all fields are strings or arrays of strings
          parsedInsights = {
            performanceAnalysis: typeof rawParsed.performanceAnalysis === 'string' 
              ? rawParsed.performanceAnalysis 
              : 'Your performance analysis shows areas for improvement.',
            subjectFocus: typeof rawParsed.subjectFocus === 'string' 
              ? rawParsed.subjectFocus 
              : 'Focus on improving weak subjects based on your performance.',
            timeManagement: typeof rawParsed.timeManagement === 'string' 
              ? rawParsed.timeManagement 
              : 'Review your time management and pacing strategies.',
            nextSteps: Array.isArray(rawParsed.nextSteps) 
              ? rawParsed.nextSteps.map(step => typeof step === 'string' ? step : 'Review and practice')
              : ["Practice more questions", "Review concepts", "Take mock tests"],
            studyStrategy: typeof rawParsed.studyStrategy === 'string' 
              ? rawParsed.studyStrategy 
              : 'Develop a systematic study plan focusing on your weak areas.'
          };
        } else {
          // Fallback: create structured insights from text
          parsedInsights = {
            performanceAnalysis: content,
            subjectFocus: "Focus on improving weak subjects",
            timeManagement: "Review time management",
            nextSteps: ["Practice more questions", "Review concepts", "Take mock tests"],
            studyStrategy: "Develop a systematic study plan"
          };
        }
      } catch (parseError) {
        // If JSON parsing fails, create structured insights
        parsedInsights = {
          performanceAnalysis: response.data.content,
          subjectFocus: "Focus on improving weak subjects",
          timeManagement: "Review time management",
          nextSteps: ["Practice more questions", "Review concepts", "Take mock tests"],
          studyStrategy: "Develop a systematic study plan"
        };
      }

      setAiInsights(parsedInsights);
    } catch (error) {
      console.error('AI Insights Error:', error);
      setAiInsightsError('Failed to generate AI insights. Please try again.');
    } finally {
      setAiInsightsLoading(false);
    }
  };



  // Prepare data for overall performance pie chart
  const pieData = {
    labels: ['Correct', 'Incorrect', 'Unattempted'],
    datasets: [
      {
        data: [
          result.correct || 0,
          result.wrong || 0,
          (result.total_questions || 0) - (result.correct || 0) - (result.wrong || 0)
        ],
        backgroundColor: ['#4caf50', '#f44336', '#9e9e9e'],
        borderColor: ['#388e3c', '#d32f2f', '#757575'],
        borderWidth: 2,
      },
    ],
  };

  // Prepare data for subject-wise performance bar chart
  const subjects = result.subjectWisePerformance ? Object.keys(result.subjectWisePerformance) : [];
  const subjectScores = subjects.map(subject => result.subjectWisePerformance[subject]);

  const barData = {
    labels: subjects,
    datasets: [
      {
        label: 'Score (%)',
        data: subjectScores,
        backgroundColor: mode === 'dark' ? 'rgba(63, 81, 181, 0.8)' : 'rgba(63, 81, 181, 0.6)',
        borderColor: '#3f51b5',
        borderWidth: 2,
        borderRadius: 8,
        borderSkipped: false,
      },
    ],
  };

  // Prepare radar data for performance metrics
  const radarData = {
    labels: ['Accuracy', 'Speed', 'Time Management', 'Subject Mastery', 'Overall Score'],
    datasets: [
      {
        label: 'Performance Metrics',
        data: [
          accuracy,
          (result.perc_score || 0) * 0.8, // Speed approximation
          (result.time_spent ? 100 - (parseInt(result.time_spent.split(':')[0]) * 60 + parseInt(result.time_spent.split(':')[1])) / 60 : 50), // Time efficiency
          subjectScores.length > 0 ? subjectScores.reduce((a, b) => a + b, 0) / subjectScores.length : 0,
          result.perc_score || 0
        ],
        backgroundColor: 'rgba(54, 162, 235, 0.2)',
        borderColor: mode === 'dark' ? '#90caf9' : '#2196f3',
        borderWidth: 2,
        pointBackgroundColor: mode === 'dark' ? '#90caf9' : '#2196f3',
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: mode === 'dark' ? '#90caf9' : '#2196f3'
      }
    ]
  };

  // Prepare time distribution data for pie chart
  const timeDistributionData = result.timeAnalysis ? {
    labels: ['Fast (<30s)', 'Medium (30-60s)', 'Slow (>60s)'],
    datasets: [
      {
        data: [
          result.timeAnalysis.timeDistribution.fast,
          result.timeAnalysis.timeDistribution.medium,
          result.timeAnalysis.timeDistribution.slow
        ],
        backgroundColor: ['#4caf50', '#ff9800', '#f44336'],
        borderColor: ['#388e3c', '#f57c00', '#d32f2f'],
        borderWidth: 2,
      },
    ],
  } : null;

  // Prepare topic performance data
  const topics = result.topicWisePerformance ? Object.keys(result.topicWisePerformance) : [];
  const topicScores = topics.map(topic => result.topicWisePerformance[topic]);

  const topicData = {
    labels: topics,
    datasets: [
      {
        label: 'Topic Performance (%)',
        data: topicScores,
        backgroundColor: mode === 'dark' ? 'rgba(76, 175, 80, 0.8)' : 'rgba(76, 175, 80, 0.6)',
        borderColor: '#4caf50',
        borderWidth: 2,
        borderRadius: 8,
        borderSkipped: false,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: {
          color: mode === 'dark' ? 'rgba(255, 255, 255, 0.87)' : 'rgba(0, 0, 0, 0.87)',
          font: { size: 12 }
        }
      },
      tooltip: {
        backgroundColor: mode === 'dark' ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.9)',
        titleColor: mode === 'dark' ? '#fff' : '#000',
        bodyColor: mode === 'dark' ? '#fff' : '#000',
        borderColor: mode === 'dark' ? '#333' : '#ddd',
        borderWidth: 1
      }
    },
    scales: {
      x: {
        ticks: {
          color: mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)'
        },
        grid: {
          color: mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
        }
      },
      y: {
        beginAtZero: true,
        max: 100,
        ticks: {
          color: mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)'
        },
        grid: {
          color: mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
        }
      }
    }
  };

  const radarOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: {
          color: mode === 'dark' ? 'rgba(255, 255, 255, 0.87)' : 'rgba(0, 0, 0, 0.87)'
        }
      }
    },
    scales: {
      r: {
        angleLines: {
          color: mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
        },
        grid: {
          color: mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
        },
        pointLabels: {
          color: mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)'
        },
        ticks: {
          color: mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
          backdropColor: 'transparent'
        }
      }
    }
  };

  return (
    <Container maxWidth={false} sx={{ mt: 4, mb: 4, px: { xs: 1, sm: 2, md: 3 } }}>
      {/* Enhanced Header Section */}
      <Box mb={4}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box>
            <Typography variant="h3" component="h1" gutterBottom sx={{ 
              fontWeight: 'bold',
              background: mode === 'dark' ? 'linear-gradient(45deg, #90caf9, #42a5f5)' : 'linear-gradient(45deg, #1976d2, #42a5f5)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              Test Result Analysis
            </Typography>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {result.TestMaster?.name || 'Test'} - Detailed Performance Breakdown
            </Typography>
          </Box>
          <Box display="flex" gap={2}>
            <Button
              component={Link}
              to="/tests"
              variant="outlined"
              startIcon={<Assignment />}
            >
              Take New Test
            </Button>
            <Button
              component={Link}
              to="/dashboard"
              variant="contained"
              startIcon={<Analytics />}
              sx={{ 
                background: 'linear-gradient(45deg, #4caf50, #66bb6a)',
                '&:hover': { background: 'linear-gradient(45deg, #388e3c, #4caf50)' }
              }}
            >
              View Dashboard
            </Button>
          </Box>
        </Box>
        
        <Card elevation={4} sx={{ 
          background: mode === 'dark' ? 'linear-gradient(135deg, #2c3e50, #34495e)' : 'linear-gradient(135deg, #f8f9ff, #e8f4fd)',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={8}>
                <Typography variant="h5" color="white" gutterBottom sx={{ fontWeight: 'bold' }}>
                  {result.TestMaster?.name || 'Test'}
                </Typography>
                <Typography variant="body1" color="rgba(255,255,255,0.8)" gutterBottom>
                  Completed on {result.test_started ? new Date(result.test_started).toLocaleDateString('en-GB', { 
                    weekday: 'long',
                    day: '2-digit', 
                    month: 'long', 
                    year: 'numeric' 
                  }) : 'Invalid Date'}
            </Typography>
                <Box display="flex" gap={2} mt={2}>
                  <Chip 
                    label={`${result.total_questions || 0} Questions`}
                    color="primary"
                    variant="outlined"
                    sx={{ color: 'white', borderColor: 'white' }}
                  />
                  <Chip 
                    label={`${formatTimeFromString(result.time_spent)} Duration`}
                    color="primary"
                    variant="outlined"
                    sx={{ color: 'white', borderColor: 'white' }}
                  />
                  <Chip 
                    label={getPerformanceLevel(result.perc_score || 0)}
                    color={getPerformanceColor(result.perc_score || 0)}
                    variant="outlined"
                    sx={{ color: 'white', borderColor: 'white' }}
                  />
                </Box>
          </Grid>
              <Grid item xs={12} md={4} textAlign="center">
            <Box position="relative" display="inline-flex">
              <CircularProgressbar
                value={result.perc_score || 0}
                    text={`${Math.round(result.perc_score || 0)}%`}
                styles={buildStyles({
                      textSize: '24px',
                      pathColor: `rgba(255, 255, 255, ${(result.perc_score || 0) / 100})`,
                      textColor: '#fff',
                      trailColor: 'rgba(255, 255, 255, 0.3)',
                })}
              />
                </Box>
                <Typography variant="h6" color="white" sx={{ mt: 2, fontWeight: 'bold' }}>
                  Overall Score
                </Typography>
                <Typography variant="body2" color="rgba(255,255,255,0.8)">
                  {result.score || 0}/{result.total_score || 0} marks
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>

      {/* Enhanced Key Metrics Cards */}
      <Grid container spacing={3} mb={4}>
        {/* Score Breakdown */}
        <Grid item xs={12} sm={6} md={3}>
          <MuiTooltip 
            title={
              <Box>
                <Typography variant="subtitle2">Accuracy Breakdown</Typography>
                <Typography variant="body2">
                  • Correct: {result.correct || 0} questions<br/>
                  • Incorrect: {result.wrong || 0} questions<br/>
                  • Unattempted: {result.unattempted || 0} questions<br/>
                  • Total: {result.totalQuestions || 0} questions
                </Typography>
              </Box>
            }
            placement="top"
            arrow
          >
            <Card 
              elevation={hoveredElement === 'accuracy' ? 12 : 4} 
              sx={{ 
                aspectRatio: '1',
                background: mode === 'dark' ? 'linear-gradient(135deg, #2d5a2d, #3a6b3a)' : 'linear-gradient(135deg, #f0f8f0, #e0f2e0)',
                position: 'relative',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                cursor: 'pointer',
                transition: 'all 0.3s ease-in-out',
                transform: hoveredElement === 'accuracy' ? 'scale(1.05)' : 'scale(1)',
                '&:hover': {
                  transform: 'scale(1.05)',
                  boxShadow: '0 12px 40px rgba(0,0,0,0.2)'
                }
              }}
              onMouseEnter={() => setHoveredElement('accuracy')}
              onMouseLeave={() => setHoveredElement(null)}
            >
              <CardContent sx={{ 
                flex: 1, 
                display: 'flex', 
                flexDirection: 'column', 
                justifyContent: 'space-between',
                p: { xs: 2, sm: 2.5, md: 3 },
                '&:last-child': { pb: { xs: 2, sm: 2.5, md: 3 } }
              }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ flex: 1, width: '100%' }}>
                  <Box>
                    <Typography color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>
                      Accuracy Rate
                    </Typography>
                    <Typography variant="h3" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {accuracy.toFixed(1)}%
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={accuracy}
                      color="success"
                      sx={{ height: 6, borderRadius: 3, mb: 1 }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {result.correct || 0} correct out of {result.attempted || 0}
                    </Typography>
                  </Box>
                  <Avatar sx={{ 
                    bgcolor: 'success.main', 
                    width: 64, 
                    height: 64,
                    boxShadow: 3,
                    transition: 'all 0.3s ease-in-out',
                    transform: hoveredElement === 'accuracy' ? 'rotate(360deg)' : 'rotate(0deg)'
                  }}>
                    <CheckCircle />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </MuiTooltip>
        </Grid>

        {/* Time Management */}
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={4} sx={{ 
            aspectRatio: '1',
            background: mode === 'dark' ? 'linear-gradient(135deg, #8b5a3c, #9b6a4c)' : 'linear-gradient(135deg, #fef5f0, #ffe8e0)',
            position: 'relative',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <CardContent sx={{ 
              flex: 1, 
              display: 'flex', 
              flexDirection: 'column', 
              justifyContent: 'space-between',
              p: { xs: 2, sm: 2.5, md: 3 },
              '&:last-child': { pb: { xs: 2, sm: 2.5, md: 3 } }
            }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ flex: 1, width: '100%' }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>
                    Time Efficiency
                  </Typography>
                  <Typography variant="h3" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {formatTimeFromString(result.time_spent)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Avg: {result.total_questions ? Math.round(totalQuestionTime / result.total_questions) : 0}s per question
                  </Typography>
                </Box>
                <Avatar sx={{ 
                  bgcolor: 'warning.main', 
                  width: 64, 
                  height: 64,
                  boxShadow: 3
                }}>
                  <Timer />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Speed Analysis */}
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={4} sx={{ 
            aspectRatio: '1',
            background: mode === 'dark' ? 'linear-gradient(135deg, #4a6b8a, #5a7b9a)' : 'linear-gradient(135deg, #f0f8ff, #e0f0ff)',
            position: 'relative',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <CardContent sx={{ 
              flex: 1, 
              display: 'flex', 
              flexDirection: 'column', 
              justifyContent: 'space-between',
              p: { xs: 2, sm: 2.5, md: 3 },
              '&:last-child': { pb: { xs: 2, sm: 2.5, md: 3 } }
            }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ flex: 1, width: '100%' }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>
                    Questions/Minute
                  </Typography>
                  <Typography variant="h3" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {result.time_spent && result.total_questions ? 
                      Math.round((result.total_questions / (parseInt(result.time_spent.split(':')[0]) * 60 + parseInt(result.time_spent.split(':')[1]))) * 10) / 10 : 
                      'N/A'
                    }
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Speed: {result.speed_score || 'N/A'} q/min
                  </Typography>
                </Box>
                <Avatar sx={{ 
                  bgcolor: 'info.main', 
                  width: 64, 
                  height: 64,
                  boxShadow: 3
                }}>
                  <Speed />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Performance Level */}
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={4} sx={{ 
            aspectRatio: '1',
            background: mode === 'dark' ? 'linear-gradient(135deg, #5d4e75, #6d5e85)' : 'linear-gradient(135deg, #f8f0ff, #f0e0ff)',
            position: 'relative',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <CardContent sx={{ 
              flex: 1, 
              display: 'flex', 
              flexDirection: 'column', 
              justifyContent: 'space-between',
              p: { xs: 2, sm: 2.5, md: 3 },
              '&:last-child': { pb: { xs: 2, sm: 2.5, md: 3 } }
            }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ flex: 1, width: '100%' }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>
                    Performance Level
                  </Typography>
                  <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {getPerformanceLevel(result.perc_score || 0)}
                  </Typography>
                  <Rating 
                    value={Math.ceil((result.perc_score || 0) / 20)} 
                    readOnly 
                    size="small"
                    sx={{ color: 'gold' }}
                  />
                </Box>
                <Avatar sx={{ 
                  bgcolor: 'secondary.main', 
                  width: 64, 
                  height: 64,
                  boxShadow: 3
                }}>
                  <Star />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Enhanced Analytics Tabs */}
      <Card elevation={4} sx={{ mb: 4 }}>
        <Tabs 
          value={activeTab} 
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{ 
            borderBottom: 1, 
            borderColor: 'divider',
            '& .MuiTab-root': {
              fontWeight: 600,
              textTransform: 'none',
              fontSize: '1rem'
            }
          }}
        >
          <Tab label="Performance Overview" icon={<Analytics />} iconPosition="start" />
          <Tab label="Subject Analysis" icon={<School />} iconPosition="start" />
          <Tab label="Time Analysis" icon={<Timer />} iconPosition="start" />
          <Tab label="Question Breakdown" icon={<Assessment />} iconPosition="start" />
        </Tabs>

        <Box p={3}>
          {activeTab === 0 && (
            <Grid container spacing={3}>
              <Grid item xs={12} lg={6}>
                <Box height={400}>
                  <Typography variant="h6" gutterBottom>Question Distribution</Typography>
                  <Pie data={pieData} options={chartOptions} />
                </Box>
              </Grid>
              <Grid item xs={12} lg={6}>
                <Box height={400}>
                  <Typography variant="h6" gutterBottom>Performance Metrics</Typography>
                  <Radar data={radarData} options={radarOptions} />
                </Box>
              </Grid>
            </Grid>
          )}

          {activeTab === 1 && (
            <Grid container spacing={3}>
              <Grid item xs={12} lg={6}>
                <Box height={400}>
                  <Typography variant="h6" gutterBottom>Subject-wise Performance</Typography>
                  <Bar data={barData} options={chartOptions} />
                </Box>
              </Grid>
              <Grid item xs={12} lg={6}>
                <Box height={400}>
                  <Typography variant="h6" gutterBottom>Topic-wise Performance</Typography>
                  {topics.length > 0 ? (
                    <Bar data={topicData} options={chartOptions} />
                  ) : (
                    <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                      <Typography color="text.secondary">
                        Topic performance data not available
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} lg={6}>
                <Card elevation={2}>
                  <CardHeader title="Subject Details" avatar={<School />} />
                  <CardContent>
                    <Stack spacing={2}>
                      {subjects.map((subject, index) => {
                        const percentage = result.subjectWisePerformance?.[subject] ?? 0;
                        const insight = getPerformanceInsight(percentage);
                        return (
                          <Card 
                            key={subject}
                            elevation={hoveredElement === `subject-${index}` ? 8 : 2}
                            sx={{ 
                              cursor: 'pointer',
                              transition: 'all 0.3s ease-in-out',
                              transform: hoveredElement === `subject-${index}` ? 'translateY(-4px)' : 'none',
                              '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                              }
                            }}
                            onClick={() => handleSubjectClick(subject, { percentage, insight })}
                            onMouseEnter={() => setHoveredElement(`subject-${index}`)}
                            onMouseLeave={() => setHoveredElement(null)}
                          >
                            <CardContent sx={{ p: 2 }}>
                              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                                                                  <Box display="flex" alignItems="center" gap={1}>
                                    <School color="primary" fontSize="small" />
                                  <Typography variant="body1" fontWeight="medium" sx={{
                                    maxWidth: 200,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}>
                                    {subject}
                                  </Typography>
                                </Box>
                                <Box display="flex" alignItems="center" gap={1}>
                                  <Typography variant="h6" color="primary" fontWeight="bold">
                                    {Math.round(percentage)}%
                                  </Typography>
                                                                     {hoveredElement === `subject-${index}` && (
                                     <Zoom in={true}>
                                       <IconButton size="small" sx={{ p: 0.5 }}>
                                         <ZoomIn fontSize="small" />
                                       </IconButton>
                                     </Zoom>
                                   )}
                                </Box>
                              </Box>
                              <LinearProgress
                                variant="determinate"
                                value={percentage}
                                color={insight.color}
                                sx={{ height: 6, borderRadius: 3, mb: 1 }}
                              />
                              <Box display="flex" alignItems="center" gap={1}>
                                {insight.icon}
                                <Typography variant="caption" color="text.secondary">
                                  {insight.level}
                                </Typography>
                              </Box>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} lg={6}>
                <Card elevation={2}>
                  <CardHeader title="Topic Details" avatar={<Book />} />
                  <CardContent>
                    <Stack spacing={2}>
                      {topics.slice(0, 10).map((topic, index) => {
                        const percentage = result.topicWisePerformance?.[topic] ?? 0;
                        const insight = getPerformanceInsight(percentage);
                        return (
                          <Card 
                            key={topic}
                            elevation={hoveredElement === `topic-${index}` ? 6 : 1}
                            sx={{ 
                              cursor: 'pointer',
                              transition: 'all 0.3s ease-in-out',
                              transform: hoveredElement === `topic-${index}` ? 'translateY(-2px)' : 'none',
                              '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: '0 6px 20px rgba(0,0,0,0.12)'
                              }
                            }}
                            onClick={() => handleTopicClick(topic, { percentage, insight })}
                            onMouseEnter={() => setHoveredElement(`topic-${index}`)}
                            onMouseLeave={() => setHoveredElement(null)}
                          >
                            <CardContent sx={{ p: 1.5 }}>
                              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                                                                  <Box display="flex" alignItems="center" gap={1}>
                                    <Book color="secondary" fontSize="small" />
                                  <Typography variant="body2" fontWeight="medium" sx={{ 
                                    overflow: 'hidden', 
                                    textOverflow: 'ellipsis', 
                                    whiteSpace: 'nowrap',
                                    maxWidth: '60%'
                                  }}>
                                    {topic}
                                  </Typography>
                                </Box>
                                <Box display="flex" alignItems="center" gap={1}>
                                  <Typography variant="body1" color="primary" fontWeight="bold">
                                    {Math.round(percentage)}%
                                  </Typography>
                                                                     {hoveredElement === `topic-${index}` && (
                                     <Zoom in={true}>
                                       <IconButton size="small" sx={{ p: 0.5 }}>
                                         <ZoomIn fontSize="small" />
                                       </IconButton>
                                     </Zoom>
                                   )}
                                </Box>
                              </Box>
                              <LinearProgress
                                variant="determinate"
                                value={percentage}
                                color={insight.color}
                                sx={{ height: 4, borderRadius: 2, mb: 0.5 }}
                              />
                              <Box display="flex" alignItems="center" gap={1}>
                                {insight.icon}
                                <Typography variant="caption" color="text.secondary">
                                  {insight.level}
                                </Typography>
                              </Box>
                            </CardContent>
                          </Card>
                        );
                      })}
                      {topics.length > 10 && (
                        <Card elevation={1} sx={{ p: 2, textAlign: 'center' }}>
                          <Typography variant="body2" color="text.secondary">
                            Showing top 10 topics. Total topics: {topics.length}
                          </Typography>
                          <Button 
                            variant="outlined" 
                            size="small" 
                            sx={{ mt: 1 }}
                            onClick={() => setTopicDetailOpen(true)}
                          >
                            View All Topics
                          </Button>
                        </Card>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {activeTab === 2 && (
            <Grid container spacing={3}>
              <Grid item xs={12} lg={6}>
                <Card elevation={2}>
                  <CardHeader title="Time Management Analysis" avatar={<TimerOutlined />} />
                  <CardContent>
                    <Box display="flex" justifyContent="space-around" alignItems="center" mb={3}>
                      <Box textAlign="center">
                        <Typography variant="h4" color="primary" fontWeight="bold">
                          {result.timeAnalysis ? Math.round(result.timeAnalysis.totalTime / 60) : 0}m {result.timeAnalysis ? result.timeAnalysis.totalTime % 60 : 0}s
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total Time Spent
                        </Typography>
                      </Box>
                      <Box textAlign="center">
                        <Typography variant="h4" color="success.main" fontWeight="bold">
                          {result.timeAnalysis ? Math.round(result.timeAnalysis.averageTimePerQuestion) : 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Avg Seconds/Q
                </Typography>
              </Box>
            </Box>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                      Questions per minute: {result.timeAnalysis ? result.timeAnalysis.questionsPerMinute.toFixed(1) : 'N/A'}
            </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={result.timeAnalysis ? (result.timeAnalysis.totalTime / (result.totalQuestions * 60)) * 100 : 0}
                      color="primary"
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                  </CardContent>
                </Card>
          </Grid>
              <Grid item xs={12} lg={6}>
                <Card elevation={2}>
                  <CardHeader title="Time Distribution" avatar={<SpeedOutlined />} />
                  <CardContent>
                    {timeDistributionData ? (
                      <Box height={300}>
                        <Pie data={timeDistributionData} options={chartOptions} />
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Time distribution data not available
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Card elevation={2}>
                  <CardHeader title="Speed vs Accuracy Analysis" avatar={<Analytics />} />
                  <CardContent>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={4}>
                        <Box textAlign="center" p={2} bgcolor="success.light" borderRadius={2}>
                          <Typography variant="h4" color="success.dark" fontWeight="bold">
                            {result.speed_score || 'N/A'}
                          </Typography>
                          <Typography variant="body2" color="success.dark">
                            Questions/Minute
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Box textAlign="center" p={2} bgcolor="info.light" borderRadius={2}>
                          <Typography variant="h4" color="info.dark" fontWeight="bold">
                            {result.accuracy_rate || accuracy.toFixed(1)}%
                          </Typography>
                          <Typography variant="body2" color="info.dark">
                            Accuracy Rate
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Box textAlign="center" p={2} bgcolor="warning.light" borderRadius={2}>
                          <Typography variant="h4" color="warning.dark" fontWeight="bold">
                            {result.timeAnalysis ? result.timeAnalysis.questionsPerMinute.toFixed(1) : 'N/A'}
                          </Typography>
                          <Typography variant="body2" color="warning.dark">
                            Actual Q/Min
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                    <Box mt={3}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Speed vs Accuracy Balance:</strong> {
                          result.speed_score && result.accuracy_rate ? 
                            (result.speed_score > 2 && result.accuracy_rate > 80 ? 'Excellent - You maintain high accuracy while working quickly' : 
                             result.speed_score > 1.5 && result.accuracy_rate > 70 ? 'Good - Balanced approach to speed and accuracy' : 
                             'Needs Improvement - Focus on either speed or accuracy based on your goals') : 
                            'Not Available'
                        }
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {activeTab === 3 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card elevation={2}>
                  <CardHeader 
                    title="Question-by-Question Analysis" 
                    avatar={<Assessment />}
                    action={
                      <Typography variant="body2" color="text.secondary">
                        Showing {result.questionBreakdown ? Math.min(result.questionBreakdown.length, 20) : 0} of {result.totalQuestions || 0} questions
                      </Typography>
                    }
                  />
                  <CardContent>
                    {result.questionBreakdown && result.questionBreakdown.length > 0 ? (
                      <>
                        <Box mb={3}>
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={6} md={3}>
                              <Paper elevation={1} sx={{ p: 2, textAlign: 'center', bgcolor: 'success.light' }}>
                                <Typography variant="h6" color="success.dark">
                                  {result.correct || 0}
                                </Typography>
                                <Typography variant="body2" color="success.dark">
                                  Correct Answers
                                </Typography>
                              </Paper>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              <Paper elevation={1} sx={{ p: 2, textAlign: 'center', bgcolor: 'error.light' }}>
                                <Typography variant="h6" color="error.dark">
                                  {result.wrong || 0}
                                </Typography>
                                <Typography variant="body2" color="error.dark">
                                  Incorrect Answers
                                </Typography>
                              </Paper>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              <Paper elevation={1} sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.100' }}>
                                <Typography variant="h6" color="grey.700">
                                  {result.unattempted || 0}
                                </Typography>
                                <Typography variant="body2" color="grey.700">
                                  Unattempted
                                </Typography>
                              </Paper>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              <Paper elevation={1} sx={{ p: 2, textAlign: 'center', bgcolor: 'info.light' }}>
                                <Typography variant="h6" color="info.dark">
                                  {result.timeAnalysis ? Math.round(result.timeAnalysis.averageTimePerQuestion) : 0}s
                                </Typography>
                                <Typography variant="body2" color="info.dark">
                                  Avg Time/Q
                                </Typography>
                </Paper>
                            </Grid>
                          </Grid>
                        </Box>
                        <TableContainer>
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell>Q#</TableCell>
                                <TableCell>Your Answer</TableCell>
                                <TableCell>Correct Answer</TableCell>
                                <TableCell>Time Spent</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Subject</TableCell>
                                <TableCell>Difficulty</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {result.questionBreakdown.slice(0, 20).map((question, index) => (
                                <TableRow 
                                  key={index}
                                  sx={{ 
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease-in-out',
                                    '&:hover': {
                                      backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
                                      transform: 'scale(1.01)',
                                      boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                                    }
                                  }}
                                  onClick={() => handleQuestionClick(question)}
                                  onMouseEnter={() => setHoveredElement(`question-${index}`)}
                                  onMouseLeave={() => setHoveredElement(null)}
                                >
                                  <TableCell>
                                    <Box display="flex" alignItems="center" gap={1}>
                                      <Typography variant="body2" fontWeight="bold">
                                        {question.questionNumber}
                                      </Typography>
                                                                             {hoveredElement === `question-${index}` && (
                                         <Zoom in={true}>
                                           <IconButton size="small" sx={{ p: 0.5 }}>
                                             <ZoomIn fontSize="small" />
                                           </IconButton>
                                         </Zoom>
                                       )}
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <MuiTooltip 
                                      title={question.isAttempted ? 
                                        `Your answer: ${question.userAnswer}` : 
                                        'Not attempted'
                                      }
                                      placement="top"
                                    >
                                      <Typography 
                                        variant="body2" 
                                        color={question.isAttempted ? 'text.primary' : 'text.secondary'}
                                        fontStyle={!question.isAttempted ? 'italic' : 'normal'}
                                        sx={{
                                          maxWidth: 150,
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap'
                                        }}
                                      >
                                        {question.userAnswer}
                                      </Typography>
                                    </MuiTooltip>
                                  </TableCell>
                                  <TableCell>
                                    <MuiTooltip title={`Correct answer: ${question.correctAnswer}`} placement="top">
                                      <Typography 
                                        variant="body2" 
                                        fontWeight="bold"
                                        sx={{
                                          maxWidth: 150,
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap'
                                        }}
                                      >
                                        {question.correctAnswer}
                                      </Typography>
                                    </MuiTooltip>
                                  </TableCell>
                                  <TableCell>
                                    <MuiTooltip 
                                      title={`Time spent: ${formatTime(question.timeSpent)}`} 
                                      placement="top"
                                    >
                                                                          <Box display="flex" alignItems="center" gap={1}>
                                      <AccessTime fontSize="small" color="action" />
                                        <Typography variant="body2">
                                          {formatTime(question.timeSpent)}
                                        </Typography>
                                      </Box>
                                    </MuiTooltip>
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      label={question.status}
                                      color={question.status === 'Correct' ? 'success' : 'error'}
                                      size="small"
                                      variant={!question.isAttempted ? 'outlined' : 'filled'}
                                      icon={question.status === 'Correct' ? <CheckCircle /> : <Cancel />}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <MuiTooltip title={`Subject ${question.subject}`} placement="top">
                                      <Chip
                                        label={`S${question.subject}`}
                                        size="small"
                                        variant="outlined"
                                        color="primary"
                                        sx={{ cursor: 'pointer' }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleSubjectClick(`Subject ${question.subject}`, {});
                                        }}
                                      />
                                    </MuiTooltip>
                                  </TableCell>
                                  <TableCell>
                                    <MuiTooltip title={`Difficulty: ${question.difficulty}`} placement="top">
                                      <Chip
                                        label={question.difficulty}
                                        size="small"
                                        variant="outlined"
                                        color={getDifficultyColor(question.difficulty)}
                                      />
                                    </MuiTooltip>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                        {result.questionBreakdown.length > 20 && (
                          <Box mt={2} textAlign="center">
                            <Typography variant="body2" color="text.secondary">
                              Showing first 20 questions. Total questions: {result.questionBreakdown.length}
                            </Typography>
                          </Box>
                        )}
                      </>
                    ) : (
                      <Box textAlign="center" py={4}>
                        <Typography color="text.secondary">
                          Detailed question analysis not available
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </Box>
      </Card>

      {/* Marking Scheme Information */}
      {result.TestMaster?.ExamMaster?.marking_scheme && (
        <Card elevation={4} sx={{ mb: 4 }}>
          <CardHeader 
            title="Marking Scheme" 
            avatar={<Grade />}
            action={
              <Button variant="outlined" size="small">
                View Full Scheme
              </Button>
            }
          />
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <Paper elevation={2} sx={{ p: 3, textAlign: 'center', bgcolor: 'success.light' }}>
                  <Typography variant="h4" color="success.dark" fontWeight="bold">
                    +{result.TestMaster.ExamMaster.marking_scheme.corr || 'N/A'}
                  </Typography>
                  <Typography variant="h6" color="success.dark">
                    Correct Answer
                  </Typography>
                  <CheckCircle sx={{ fontSize: 40, color: 'success.dark', mt: 1 }} />
                </Paper>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Paper elevation={2} sx={{ p: 3, textAlign: 'center', bgcolor: 'error.light' }}>
                  <Typography variant="h4" color="error.dark" fontWeight="bold">
                    {result.TestMaster.ExamMaster.marking_scheme.incorr || 'N/A'}
                  </Typography>
                  <Typography variant="h6" color="error.dark">
                    Incorrect Answer
                  </Typography>
                  <Cancel sx={{ fontSize: 40, color: 'error.dark', mt: 1 }} />
                </Paper>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Paper elevation={2} sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.100' }}>
                  <Typography variant="h4" color="grey.700" fontWeight="bold">
                    {result.TestMaster.ExamMaster.marking_scheme.unatt || 'N/A'}
                  </Typography>
                  <Typography variant="h6" color="grey.700">
                    Unattempted
                  </Typography>
                  <Warning sx={{ fontSize: 40, color: 'grey.700', mt: 1 }} />
                </Paper>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* AI Insights & Recommendations */}
      <Card ref={insightsRef} elevation={4} sx={{ mb: 4 }}>
        <CardHeader 
          title="AI-Powered Insights" 
          subheader="Personalized recommendations for improvement"
          avatar={<PsychologyOutlined />}
          action={
            <Box display="flex" alignItems="center" gap={1}>
              {aiInsightsLoading && (
                <CircularProgress size={20} />
              )}
              {aiInsightsError && (
                <IconButton 
                  size="small" 
                  onClick={generateAIInsights}
                  color="error"
                >
                  <Refresh />
                </IconButton>
              )}
              <FormControlLabel
                control={
                  <Switch
                    checked={insightsVisible}
                    onChange={(e) => setInsightsVisible(e.target.checked)}
                    size="small"
                  />
                }
                label="Enable AI Insights"
                labelPlacement="start"
              />
            </Box>
          }
        />
        <CardContent>
          {!insightsVisible ? (
            <Box textAlign="center" py={4}>
              <PsychologyOutlined sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                AI Insights Disabled
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                Enable AI insights to get personalized recommendations based on your performance
              </Typography>
              <Button
                variant="contained"
                startIcon={<PsychologyOutlined />}
                onClick={() => setInsightsVisible(true)}
                sx={{ 
                  background: 'linear-gradient(45deg, #2196F3, #21CBF3)',
                  '&:hover': { background: 'linear-gradient(45deg, #1976D2, #1E88E5)' }
                }}
              >
                Enable AI Insights
              </Button>
            </Box>
          ) : aiInsightsLoading ? (
            <Box textAlign="center" py={4}>
              <CircularProgress size={60} sx={{ mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Generating AI Insights
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Analyzing your performance with advanced AI...
              </Typography>
            </Box>
          ) : aiInsightsError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              <AlertTitle>AI Insights Error</AlertTitle>
              <Typography variant="body2" mb={2}>
                {aiInsightsError}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={generateAIInsights}
                startIcon={<Refresh />}
              >
                Retry
              </Button>
            </Alert>
          ) : aiInsights ? (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <AlertTitle>Performance Analysis</AlertTitle>
                  <Typography variant="body2">
                    {typeof aiInsights.performanceAnalysis === 'string' 
                      ? aiInsights.performanceAnalysis 
                      : 'Your performance analysis is being processed...'}
                  </Typography>
                </Alert>
                
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <AlertTitle>Subject Focus</AlertTitle>
                  <Typography variant="body2">
                    {typeof aiInsights.subjectFocus === 'string' 
                      ? aiInsights.subjectFocus 
                      : 'Focus on improving weak subjects based on your performance.'}
                  </Typography>
                </Alert>

                <Alert severity="success" sx={{ mb: 2 }}>
                  <AlertTitle>Study Strategy</AlertTitle>
                  <Typography variant="body2">
                    {typeof aiInsights.studyStrategy === 'string' 
                      ? aiInsights.studyStrategy 
                      : 'Develop a systematic study plan focusing on your weak areas.'}
                  </Typography>
                </Alert>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Alert severity="success" sx={{ mb: 2 }}>
                  <AlertTitle>Time Management</AlertTitle>
                  <Typography variant="body2">
                    {typeof aiInsights.timeManagement === 'string' 
                      ? aiInsights.timeManagement 
                      : 'Review your time management and pacing strategies.'}
                  </Typography>
                </Alert>
                
                <Alert severity="info" sx={{ mb: 2 }}>
                  <AlertTitle>Next Steps</AlertTitle>
                  <Typography variant="body2">
                    {Array.isArray(aiInsights.nextSteps) ? (
                      aiInsights.nextSteps.map((step, index) => (
                        <Box key={index} display="flex" alignItems="center" gap={1} mb={0.5}>
                          <Box 
                            sx={{ 
                              width: 6, 
                              height: 6, 
                              borderRadius: '50%', 
                              bgcolor: 'primary.main' 
                            }} 
                          />
                          {typeof step === 'string' ? step : 'Review and practice'}
                        </Box>
                      ))
                    ) : typeof aiInsights.nextSteps === 'string' ? (
                      aiInsights.nextSteps
                    ) : (
                      <Box>
                        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.main' }} />
                          Review incorrect answers to understand mistakes
                        </Box>
                        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.main' }} />
                          Practice more questions in weak subjects
                        </Box>
                        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.main' }} />
                          Take similar tests to improve speed and accuracy
                        </Box>
                      </Box>
                    )}
                  </Typography>
                </Alert>

                <Card elevation={2} sx={{ p: 2, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    💡 AI Recommendation
                  </Typography>
                  <Typography variant="body2">
                    Based on your performance patterns, consider focusing on your weakest subjects first and gradually building up to more complex topics.
                  </Typography>
                </Card>
              </Grid>
            </Grid>
          ) : (
            <Box textAlign="center" py={4}>
              <Typography variant="body2" color="text.secondary">
                Click "Generate Insights" to get AI-powered recommendations
              </Typography>
              <Button
                variant="outlined"
                onClick={generateAIInsights}
                startIcon={<PsychologyOutlined />}
                sx={{ mt: 2 }}
              >
                Generate AI Insights
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

            {/* Quick Actions */}
      <Card elevation={4}>
        <CardContent>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
            What's Next?
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                component={Link}
                to="/tests"
                variant="contained"
                startIcon={<PlayArrow />}
                fullWidth
                sx={{ 
                  background: 'linear-gradient(45deg, #4caf50, #66bb6a)',
                  '&:hover': { background: 'linear-gradient(45deg, #388e3c, #4caf50)' },
                  py: 2
                }}
              >
                Take New Test
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                component={Link}
                to="/dashboard"
                variant="outlined"
                startIcon={<Analytics />}
                fullWidth
                sx={{ py: 2 }}
              >
                View Dashboard
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                component={Link}
                to="/ai-assistant"
                variant="outlined"
                startIcon={<Psychology />}
                fullWidth
                sx={{ py: 2 }}
              >
                Get AI Help
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                component={Link}
                to="/performance"
                variant="outlined"
                startIcon={<Timeline />}
                fullWidth
                sx={{ py: 2 }}
              >
                Performance History
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Enhanced Question Detail Popup */}
      <Dialog
        open={questionDetailOpen}
        onClose={() => setQuestionDetailOpen(false)}
        maxWidth="md"
        fullWidth
        TransitionComponent={Zoom}
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: mode === 'dark' ? 'linear-gradient(135deg, #2c3e50, #34495e)' : 'linear-gradient(135deg, #f8f9ff, #e8f4fd)'
          }
        }}
      >
        {selectedQuestion && (
          <>
            <DialogTitle sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              color: 'white'
            }}>
              <Box>
                <Typography variant="h5" fontWeight="bold">
                  Question {selectedQuestion.questionNumber} Analysis
                </Typography>
                <Typography variant="body2" color="rgba(255,255,255,0.8)">
                  Detailed breakdown and insights
                </Typography>
              </Box>
              <IconButton 
                onClick={() => setQuestionDetailOpen(false)}
                sx={{ color: 'white' }}
              >
                <Cancel />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card elevation={2} sx={{ p: 2, mb: 2 }}>
                    <Typography variant="h6" gutterBottom>Performance</Typography>
                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                      <Chip
                        label={selectedQuestion.status}
                        color={selectedQuestion.status === 'Correct' ? 'success' : 'error'}
                        icon={selectedQuestion.status === 'Correct' ? <CheckCircle /> : <Cancel />}
                      />
                      <Chip
                        label={selectedQuestion.difficulty}
                        color={getDifficultyColor(selectedQuestion.difficulty)}
                        variant="outlined"
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {selectedQuestion.isAttempted ? 
                        `You answered: "${selectedQuestion.userAnswer}"` : 
                        'This question was not attempted'
                      }
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Correct answer: "{selectedQuestion.correctAnswer}"
                    </Typography>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card elevation={2} sx={{ p: 2, mb: 2 }}>
                    <Typography variant="h6" gutterBottom>Time Analysis</Typography>
                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                      <AccessTime color="primary" />
                      <Typography variant="h4" fontWeight="bold">
                        {formatTime(selectedQuestion.timeSpent)}
                      </Typography>
                    </Box>
                    {selectedQuestion.isAttempted && (
                      <Chip
                        label={getTimeInsight(selectedQuestion.timeSpent, selectedQuestion.difficulty).insight}
                        color={getTimeInsight(selectedQuestion.timeSpent, selectedQuestion.difficulty).color}
                        size="small"
                      />
                    )}
                  </Card>
                </Grid>
                <Grid item xs={12}>
                  <Card elevation={2} sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>Subject & Topic</Typography>
                    <Box display="flex" gap={2}>
                      <Chip
                        label={`Subject ${selectedQuestion.subject}`}
                        color="primary"
                        variant="outlined"
                        onClick={() => handleSubjectClick(`Subject ${selectedQuestion.subject}`, {})}
                        sx={{ cursor: 'pointer' }}
                      />
                      <Chip
                        label={`Topic ${selectedQuestion.topic}`}
                        color="secondary"
                        variant="outlined"
                        onClick={() => handleTopicClick(`Topic ${selectedQuestion.topic}`, {})}
                        sx={{ cursor: 'pointer' }}
                      />
                    </Box>
                  </Card>
                </Grid>
              </Grid>
            </DialogContent>
          </>
        )}
      </Dialog>

      {/* Speed Dial for Quick Actions */}
      <SpeedDial
        ariaLabel="Quick Actions"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        icon={<SpeedDialIcon />}
        open={speedDialOpen}
        onOpen={() => setSpeedDialOpen(true)}
        onClose={() => setSpeedDialOpen(false)}
      >
        {speedDialActions.map((action) => (
          <SpeedDialAction
            key={action.name}
            icon={action.icon}
            tooltipTitle={action.name}
            onClick={action.action}
          />
        ))}
      </SpeedDial>

      {/* Settings Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: { width: 320 }
        }}
      >
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>Settings</Typography>
          <FormControlLabel
            control={
              <Switch
                checked={showAdvancedMetrics}
                onChange={(e) => setShowAdvancedMetrics(e.target.checked)}
              />
            }
            label="Show Advanced Metrics"
          />
          <FormControlLabel
            control={
              <Switch
                checked={comparisonMode}
                onChange={(e) => setComparisonMode(e.target.checked)}
              />
            }
            label="Comparison Mode"
          />
        </Box>
      </Drawer>
    </Container>
  );
};

export default TestResult;
